import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useModalKeyboardHandlers } from "../../useModalKeyboardHandlers";
import * as metadataService from "../../../services/metadataService";

export default function SolventModal({ solventList, onClose, onSave }) {
    const [solventOptions, setSolventOptions] = useState([]);

    useEffect(() => {
        const loadSolvents = async () => {
            try {
                console.log('Fetching solvents from database...');
                const data = await metadataService.getAllSolvents();
                console.log('Solvents fetched:', data);
                // Map to { value, label } for react-select
                const options = (data || []).map(s => ({ value: s.solventName, label: s.solventName }));
                setSolventOptions(options);
            } catch (err) {
                console.error("Failed to load solvents:", err);
            }
        };
        loadSolvents();
    }, []);

    const createEmptySolvent = () => ({
        solventName: "",
        weight: "",
        weightUnit: "gram",
        volumeRatio: "", // empty string for controlled input
        volumeRatioUnit: "v/v",
    });

    const [solvents, setSolvents] = useState(
        Array.isArray(solventList) && solventList.length > 0
            ? solventList.map(s => ({ ...s, volumeRatio: s.volumeRatio ?? "" }))
            : [createEmptySolvent()]
    );
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        const initialized =
            Array.isArray(solventList) && solventList.length > 0
                ? solventList.map(s => ({ ...s, volumeRatio: s.volumeRatio ?? "" }))
                : [createEmptySolvent()];
        setSolvents(initialized);
        setErrors([]);
    }, [solventList]);

    const handleChange = (index, e) => {
        const { name, value } = e.target;
        setSolvents(prev => {
            const updated = [...prev];
            updated[index][name] = value;
            return updated;
        });

        setErrors(prev => {
            const updatedErrors = [...prev];
            if (updatedErrors[index]) updatedErrors[index][name] = null;
            return updatedErrors;
        });
    };

    const blockInvalidChars = (e) => {
        if (["e", "E", "-", "+"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleAddSolvent = () => {
        setSolvents(prev => [...prev, createEmptySolvent()]);
        setErrors(prev => [...prev, {}]);
    };

    const handleRemoveSolvent = (index) => {
        setSolvents(prev => {
            const updated = prev.filter((_, i) => i !== index);
            return updated.length ? updated : [createEmptySolvent()];
        });
        setErrors(prev => {
            const updatedErrors = prev.filter((_, i) => i !== index);
            return updatedErrors.length ? updatedErrors : [{}];
        });
    };

    const validate = () => {
        const newErrors = [];
        const multipleSolvents = solvents.length > 1;
        let totalVolumeRatio = 0;
        let volumeRatioError = false;

        solvents.forEach((s, i) => {
            const err = {};

            if (!s.solventName) err.solventName = "Solvent is required";

            if (s.weight !== "" && s.weight !== null) {
                const val = Number(s.weight);
                if (val === 0) {
                    err.weight = "Weight cannot be zero";
                } else if (val < 0) {
                    err.weight = "Weight must be a positive number";
                }
            }

            if (multipleSolvents) {
                if (s.volumeRatio === "" || isNaN(s.volumeRatio)) {
                    err.volumeRatio = "Volume ratio is required";
                    volumeRatioError = true;
                } else {
                    const val = Number(s.volumeRatio);
                    if (val === 0) {
                        err.volumeRatio = "Volume ratio cannot be zero";
                        volumeRatioError = true;
                    } else if (val < 0) {
                        err.volumeRatio = "Volume ratio must be a positive number";
                        volumeRatioError = true;
                    } else {
                        totalVolumeRatio += val;
                    }
                }
            }

            newErrors.push(err);
        });

        if (multipleSolvents && !volumeRatioError && totalVolumeRatio === 0) {
            solvents.forEach((_, i) => {
                newErrors[i].volumeRatio = "Total ratio must be greater than 0";
            });
        }

        setErrors(newErrors);
        return !newErrors.some(e => Object.keys(e).length > 0);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        const normalizedSolvents = solvents.map(s => ({
            ...s,
            volumeRatio: solvents.length === 1 ? 100 : Number(s.volumeRatio),
        }));

        onSave(normalizedSolvents);
        onClose();
    };

    const handleKeyboardSave = () => {
        if (validate()) {
            const normalizedSolvents = solvents.map(s => ({
                ...s,
                volumeRatio: solvents.length === 1 ? 100 : Number(s.volumeRatio),
            }));
            onSave(normalizedSolvents);
            onClose();
        }
    };

    useModalKeyboardHandlers({
        onClose,
        onSave: handleKeyboardSave,
        dependencies: [solvents],
    });

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal-content shadow-lg border-0" onSubmit={handleSubmit}>
                    <div className="modal-header bg-light">
                        <h5 className="modal-title fw-bold text-dark">
                            <i className="bi bi-droplet-half me-2 text-info"></i>
                            Solvent Configuration
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body p-4 bg-light-subtle">
                        {solvents.map((solvent, index) => (
                            <div key={index} className="card border-light shadow-sm mb-4">
                                <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                                    <span className="fw-bold text-info">Solvent Component #{index + 1}</span>
                                    {solvents.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                            onClick={() => handleRemoveSolvent(index)}
                                            title="Remove Solvent"
                                        >
                                            <i className="bi bi-trash me-1"></i> Remove
                                        </button>
                                    )}
                                </div>
                                <div className="card-body p-4">
                                    <div className="row g-4">
                                        {/* Solvent Name */}
                                        <div className="col-12">
                                            <label className="form-label fw-bold text-secondary">
                                                Solvent Name <span className="text-danger">*</span>
                                            </label>
                                            <Select
                                                name="solventName"
                                                value={solventOptions.find(s => s.value === solvent.solventName) || null}
                                                onChange={(selected) =>
                                                    handleChange(index, {
                                                        target: { name: "solventName", value: selected?.value || "" }
                                                    })
                                                }
                                                options={solventOptions}
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '38px',
                                                        borderColor: '#dee2e6',
                                                        boxShadow: 'none',
                                                        '&:hover': { borderColor: '#adb5bd' }
                                                    }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                                }}
                                                menuPortalTarget={document.body}
                                                placeholder="Search or Select Solvent..."
                                                isSearchable
                                                className={errors[index]?.solventName ? "is-invalid" : ""}
                                            />
                                            {errors[index]?.solventName && (
                                                <div className="text-danger small mt-1">
                                                    <i className="bi bi-exclamation-circle me-1"></i>
                                                    {errors[index].solventName}
                                                </div>
                                            )}
                                        </div>

                                        {/* Weight */}
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold text-secondary">Weight (g)</label>
                                            <input
                                                type="number"
                                                step="any"
                                                min="0"
                                                name="weight"
                                                className={`form-control shadow-sm ${errors[index]?.weight ? "is-invalid" : ""}`}
                                                value={solvent.weight}
                                                onChange={(e) => handleChange(index, e)}
                                                onKeyDown={blockInvalidChars}
                                                placeholder="Input: 10"
                                            />
                                            {errors[index]?.weight && (
                                                <div className="text-danger small mt-1">
                                                    <i className="bi bi-exclamation-circle me-1"></i>
                                                    {errors[index].weight}
                                                </div>
                                            )}
                                        </div>

                                        {/* Volume Ratio - Only if multiple */}
                                        {solvents.length > 1 && (
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">
                                                    Volume Ratio (Parts) <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    step="any"
                                                    min="0"
                                                    name="volumeRatio"
                                                    className={`form-control shadow-sm ${errors[index]?.volumeRatio ? "is-invalid" : ""}`}
                                                    value={solvent.volumeRatio ?? ""}
                                                    onChange={(e) => handleChange(index, e)}
                                                    onKeyDown={blockInvalidChars}
                                                    placeholder="e.g. 3"
                                                    required
                                                />
                                                {errors[index]?.volumeRatio && (
                                                    <div className="text-danger small mt-1">
                                                        <i className="bi bi-exclamation-circle me-1"></i>
                                                        {errors[index].volumeRatio}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Add Button */}
                        <button
                            type="button"
                            className="btn btn-outline-info w-100 py-2 border-dashed"
                            style={{ borderStyle: 'dashed' }}
                            onClick={handleAddSolvent}
                        >
                            <i className="bi bi-plus-lg me-2"></i>
                            Add Another Solvent Component
                        </button>
                    </div>

                    <div className="modal-footer bg-light border-top-0 py-3">
                        <button type="button" className="btn btn-outline-secondary px-4 rounded-pill fw-bold me-2" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary px-4 shadow-sm fw-bold rounded-pill">
                            <i className="bi bi-check2-circle me-1"></i>
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
